import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { useNavigate } from 'react-router-dom';
import PlayerName, { usePlayerDecorations } from '../Common/PlayerName.tsx';
import { API_URL } from '../../config/api.ts';
import './ValentineDailyDuo.css';

const QUESTION_TIME = 20; // seconds

interface DailyQuestion {
  index: number;
  id: number;
  question: string;
  options: string[];
  correct_answer_index: number; // 1-based
  imageUrl?: string;
  difficulty: number;
  category: string;
  category_name?: string;
  category_emoji?: string;
  series?: string;
}

const getCategoryDisplay = (question: DailyQuestion) => {
  if (question.category_name && question.category_emoji) {
    return { name: question.category_name, emoji: question.category_emoji };
  }
  const categoryMap: Record<string, { name: string; emoji: string }> = {
    'arts': { name: 'Arts & Culture', emoji: '🎨' },
    'entertainment': { name: 'Divertissement', emoji: '🎬' },
    'geography': { name: 'Géographie', emoji: '🌍' },
    'history': { name: 'Histoire', emoji: '📚' },
    'science': { name: 'Sciences', emoji: '🔬' },
    'sports': { name: 'Sports', emoji: '⚽' },
    'mystery': { name: 'Mystère', emoji: '🔮' },
  };
  const standard = categoryMap[question.category?.toLowerCase()];
  if (standard) return standard;
  return { name: 'Catégorie', emoji: '📝' };
};

const ValentineDailyDuo: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(1);
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState<DailyQuestion | null>(null);
  const [timer, setTimer] = useState(QUESTION_TIME);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [endReason, setEndReason] = useState<'wrong' | 'timeout' | 'cheat' | 'already_played' | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<string>('');
  const [nextDailyCountdown, setNextDailyCountdown] = useState<string>('');
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [mySeries, setMySeries] = useState<string>('A');
  const [partnerScore, setPartnerScore] = useState<any>(null);
  const [soloLeaderboard, setSoloLeaderboard] = useState<any[]>([]);
  const [couplesLeaderboard, setCouplesLeaderboard] = useState<any[]>([]);
  const [pairId, setPairId] = useState<number | null>(null);
  const startTsRef = useRef<number>(0);
  const timerRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);

  // Pre-compute heart positions (stable across re-renders)
  const heartData = useMemo(() => Array.from({ length: 15 }).map((_, i) => ({
    left: (i * 17 + 3) % 100,
    delay: (i * 0.8) % 6,
    duration: 6 + (i * 1.3) % 4,
    size: 14 + (i * 2.5) % 16,
    opacity: 0.12 + (i * 0.015) % 0.12,
    emoji: ['💕', '💗', '💖', '💘', '💝', '❤️'][i % 6],
  })), []);

  // Leaderboard decorations
  const leaderboardUserIds = soloLeaderboard.map((r: any) => r.user_id);
  const { decorations } = usePlayerDecorations(leaderboardUserIds);

  // Auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Anti-cheat: auto-submit if tab hidden while a question is active
  useEffect(() => {
    function onVisibility() {
      if (document.hidden && running) {
        handleGameOver('cheat');
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [running]);

  // Countdown to next daily
  useEffect(() => {
    function updateCountdown() {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      tomorrow.setUTCHours(0, 0, 0, 0);
      const timeLeft = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      setNextDailyCountdown(
        `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      );
    }
    if (gameEnded) {
      updateCountdown();
      countdownRef.current = setInterval(updateCountdown, 1000);
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [gameEnded]);

  // Fetch status
  async function fetchStatus() {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/valentine/daily/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMySeries(data?.series || 'A');
      setPairId(data?.pairId || null);
      setSoloLeaderboard(data?.soloTop || []);
      setCouplesLeaderboard(data?.couplesTop || []);
      setPartnerScore(data?.partnerScore || null);

      if (data?.mine) {
        setAlreadyPlayed(true);
        setTodayScore(data.mine.score);
        setGameEnded(true);
        setEndReason('already_played');
      }
    } catch (e) {
      // ignore
    }
  }

  // Fetch next question in streak
  async function fetchQuestion(i: number) {
    if (!user) return;
    setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/valentine/daily/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ index: i, language: 'fr' }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || 'Échec de récupération de la question');
      }
      const q: DailyQuestion = await res.json();
      setQuestion(q);
      setTimer(QUESTION_TIME);
      setRunning(true);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
      setQuestion(null);
      setRunning(false);
    }
  }

  // Submit score
  async function submitScore(finalScore: number) {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      let durationSec = startTsRef.current > 0 ? Math.floor((Date.now() - startTsRef.current) / 1000) : 0;
      if (durationSec === 0 && startTsRef.current > 0) durationSec = 1;
      durationSec = Math.max(1, durationSec);
      const res = await fetch(`${API_URL}/api/valentine/daily/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score: finalScore, durationSec }),
      });
      const data = await res.json();
      if (data?.partnerScore) setPartnerScore(data.partnerScore);
      if (data?.soloTop) setSoloLeaderboard(data.soloTop);
      await fetchStatus();
    } catch (e) {
      // ignore
    }
  }

  // Game over
  function handleGameOver(reason: 'wrong' | 'timeout' | 'cheat' | 'already_played' = 'wrong') {
    setRunning(false);
    setGameEnded(true);
    setEndReason(reason);
    if (question && reason === 'wrong') {
      const correctIndex = question.correct_answer_index - 1;
      setCorrectAnswer(question.options[correctIndex] || '');
    }
    if (reason !== 'already_played') {
      submitScore(score);
    }
  }

  // Initial load
  useEffect(() => {
    if (!loading && user) {
      fetchStatus();
    }
  }, [loading, user]);

  // Start game if not already played
  useEffect(() => {
    if (!loading && user && !alreadyPlayed) {
      startTsRef.current = Date.now();
      fetchQuestion(1);
    }
  }, [loading, user, alreadyPlayed]);

  // Timer
  useEffect(() => {
    if (!running) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleGameOver('timeout');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  // Handle answer
  function handleAnswer(choiceIndex: number) {
    if (!question || showAnswerFeedback) return;
    setSelectedAnswer(choiceIndex);
    setRunning(false);
    clearInterval(timerRef.current);
    const isCorrect = (choiceIndex + 1) === Number(question.correct_answer_index || 0);
    setLastAnswerCorrect(isCorrect);
    setShowAnswerFeedback(true);

    setTimeout(() => {
      setShowAnswerFeedback(false);
      setSelectedAnswer(null);
      setLastAnswerCorrect(null);
      if (!isCorrect) {
        const correctIndex = question.correct_answer_index - 1;
        setCorrectAnswer(question.options[correctIndex] || '');
        handleGameOver('wrong');
      } else {
        setScore((s) => s + 1);
        const next = index + 1;
        setIndex(next);
        setQuestion(null);
        fetchQuestion(next);
      }
    }, 2000);
  }

  if (loading) return <div style={{ padding: 20 }}>Chargement…</div>;
  if (!user) return null;

  return (
    <div className="valentine-daily-container">
      {/* Floating hearts background */}
      <div className="valentine-daily-hearts-bg">
        {heartData.map((h, i) => (
          <span key={i} className="valentine-floating-heart" style={{
            left: `${h.left}%`,
            animationDelay: `${h.delay}s`,
            animationDuration: `${h.duration}s`,
            fontSize: `${h.size}px`,
            opacity: h.opacity,
          }}>
            {h.emoji}
          </span>
        ))}
      </div>

      <div className="valentine-daily-header">
        <h2 className="valentine-daily-title">💕 Daily Février</h2>
        <div className="valentine-daily-nav">
          {pairId && (
            <span className="valentine-daily-series-badge">
              Série {mySeries}
            </span>
          )}
          <button onClick={() => navigate('/')} className="valentine-daily-back-btn">
            ← Retour
          </button>
        </div>
      </div>

      <div className="valentine-daily-description">
        <p>
          💕 <strong>Daily Février</strong> : Streak jusqu'à l'échec •
          ⏰ 20 secondes par question •
          🚫 Changer d'onglet met fin à la partie
          {pairId && <> • 💑 En couple : chacun a sa propre série !</>}
        </p>
      </div>

      <div className="valentine-daily-content">
        <div className="valentine-daily-game">
          {/* Score + Question number */}
          {!alreadyPlayed && (
            <div className="valentine-daily-stats">
              <div className="valentine-daily-stat">
                <span className="valentine-daily-stat-value">{score}</span>
                <span className="valentine-daily-stat-label">Score</span>
              </div>
              <div className="valentine-daily-stat">
                <span className="valentine-daily-stat-value">#{index}</span>
                <span className="valentine-daily-stat-label">Question</span>
              </div>
            </div>
          )}

          {/* Timer */}
          {!alreadyPlayed && running && (
            <div className="valentine-timer-container">
              <div className="valentine-timer-header">
                <span className="valentine-timer-label">Temps restant</span>
                <span className={`valentine-timer-value ${timer <= 5 ? 'warning' : ''}`}>
                  ⏱️ {timer}s
                </span>
              </div>
              <div className="valentine-timer-bar">
                <div
                  className={`valentine-timer-fill ${timer <= 5 ? 'warning' : ''}`}
                  style={{ width: `${(timer / QUESTION_TIME) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && !alreadyPlayed && (
            <div className="valentine-daily-error">❌ {error}</div>
          )}

          {/* Question card */}
          {question && !alreadyPlayed ? (
            <div className="valentine-daily-question-card">
              <div className="valentine-daily-question-meta">
                <span className="valentine-daily-question-category">
                  {(() => {
                    const cat = getCategoryDisplay(question);
                    return `${cat.emoji} ${cat.name}`;
                  })()}
                </span>
                <div className="valentine-daily-question-difficulty">
                  {Array.from({ length: question.difficulty }, (_, i) => (
                    <span key={i} className="difficulty-heart">💗</span>
                  ))}
                </div>
              </div>

              <div className="valentine-daily-question-text">
                {question.question}
              </div>

              {question.imageUrl && (
                <div className="valentine-daily-question-image">
                  <img
                    src={question.imageUrl}
                    alt="Question illustration"
                    style={{
                      maxWidth: '300px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      objectFit: 'contain',
                      border: '2px solid #ff6b8a',
                      margin: '16px auto',
                      display: 'block',
                    }}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}

              <div className="valentine-daily-options">
                {question.options?.map((opt: string, i: number) => {
                  let btnClass = "valentine-daily-option";
                  if (showAnswerFeedback) {
                    const cIdx = question.correct_answer_index - 1;
                    if (i === cIdx) btnClass += " correct-answer";
                    else if (i === selectedAnswer) btnClass += " wrong-answer";
                  }
                  return (
                    <button
                      key={`${question.id}-${index}-${i}-${opt.substring(0, 10)}`}
                      onClick={() => handleAnswer(i)}
                      className={btnClass}
                      disabled={!running || showAnswerFeedback}
                    >
                      <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                      {opt}
                      {showAnswerFeedback && i === question.correct_answer_index - 1 && (
                        <span className="feedback-icon">✓</span>
                      )}
                      {showAnswerFeedback && i === selectedAnswer && i !== question.correct_answer_index - 1 && (
                        <span className="feedback-icon">✗</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (!gameEnded && !alreadyPlayed) ? (
            <div className="valentine-daily-loading">💕 Chargement de la question...</div>
          ) : null}

          {/* Game over / already played */}
          {gameEnded && (
            <div className="valentine-daily-game-over">
              <div className="game-over-content">
                <div className="game-over-icon">
                  {endReason === 'wrong' && '💔'}
                  {endReason === 'timeout' && '⏰'}
                  {endReason === 'cheat' && '🚫'}
                  {endReason === 'already_played' && '💕'}
                </div>

                <h3 className="game-over-title">
                  {endReason === 'wrong' && 'Cœur brisé !'}
                  {endReason === 'timeout' && 'Temps écoulé !'}
                  {endReason === 'cheat' && 'Triche détectée !'}
                  {endReason === 'already_played' && 'Déjà joué aujourd\'hui !'}
                </h3>

                <div className="game-over-stats">
                  <div className="final-score">
                    <span className="score-label">
                      {endReason === 'already_played' ? 'Score d\'aujourd\'hui' : 'Score final'}
                    </span>
                    <span className="score-value valentine-score">
                      {endReason === 'already_played' ? (todayScore || 0) : score}
                    </span>
                    <span className="score-questions">
                      question{((endReason === 'already_played' ? (todayScore || 0) : score) > 1 ? 's' : '')} réussie{((endReason === 'already_played' ? (todayScore || 0) : score) > 1 ? 's' : '')}
                    </span>
                  </div>

                  {endReason === 'wrong' && correctAnswer && (
                    <div className="correct-answer-reveal">
                      <span className="correct-label">La bonne réponse était :</span>
                      <span className="correct-value">✅ {correctAnswer}</span>
                    </div>
                  )}

                  {/* Partner score section */}
                  {partnerScore && (
                    <div className="partner-score-section">
                      <h4>💑 Score de ton partenaire</h4>
                      <div className="partner-score-card">
                        <span className="partner-name">{partnerScore.user_name}</span>
                        <span className="partner-score-value">{partnerScore.score} pts</span>
                        <span className="partner-duration">{partnerScore.duration_sec}s</span>
                      </div>
                      <div className="combined-score">
                        Score combiné : <strong>
                          {(endReason === 'already_played' ? (todayScore || 0) : score) + partnerScore.score}
                        </strong> pts 💕
                      </div>
                    </div>
                  )}
                  {pairId && !partnerScore && (
                    <div className="partner-waiting">
                      💑 Ton partenaire n'a pas encore joué aujourd'hui...
                    </div>
                  )}
                </div>

                <div className="next-challenge">
                  <div className="countdown-container valentine-countdown">
                    <h4>⏳ Prochain Daily Février dans :</h4>
                    <div className="countdown-timer">{nextDailyCountdown}</div>
                    <p className="countdown-hint">
                      Reviens demain pour une nouvelle série de questions ! 💕
                    </p>
                  </div>
                </div>

                <div className="game-over-actions">
                  <button onClick={() => navigate('/')} className="valentine-daily-home-btn">
                    🏠 Retour à l'accueil
                  </button>
                </div>
              </div>
            </div>
          )}

          {!running && !gameEnded && question && (
            <div className="valentine-daily-loading">💕 En attente...</div>
          )}
        </div>

        {/* Leaderboards */}
        <div className="valentine-daily-leaderboards">
          {/* Solo leaderboard */}
          <div className="valentine-daily-leaderboard">
            <h3>🏆 Top Solo du jour</h3>
            {soloLeaderboard.length === 0 ? (
              <div className="leaderboard-empty">💕 Soyez le premier à jouer !</div>
            ) : (
              <ol className="leaderboard-list">
                {soloLeaderboard.map((r: any, i: number) => (
                  <li key={i} className={`leaderboard-item ${i < 3 ? 'top-three' : ''}`}>
                    <span className="leaderboard-rank">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </span>
                    <PlayerName
                      userId={r.user_id}
                      userName={r.user_name || r.user_id}
                      decoration={decorations[r.user_id]}
                      className="leaderboard-name"
                    />
                    <span className="leaderboard-score">
                      {r.score} pts • {r.duration_sec}s
                      {r.series && <span className="leaderboard-series-badge">{r.series}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Couples leaderboard */}
          {couplesLeaderboard.length > 0 && (
            <div className="valentine-daily-leaderboard couples-leaderboard">
              <h3>💑 Top Couples du jour</h3>
              <ol className="leaderboard-list">
                {couplesLeaderboard.map((c: any, i: number) => (
                  <li key={i} className={`leaderboard-item ${i < 3 ? 'top-three' : ''}`}>
                    <span className="leaderboard-rank">
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </span>
                    <span className="leaderboard-couple-names">
                      {c.user1_name} & {c.user2_name}
                    </span>
                    <span className="leaderboard-score">
                      {c.combined_score} pts ({c.score1} + {c.score2})
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ValentineDailyDuo;
